import React, { useState, useEffect, useRef } from 'react';
import { Fab, Zoom, useTheme, keyframes } from '@mui/material';
import { styled } from '@mui/material/styles';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

// 定义动画关键帧
const rocketLaunch = keyframes`
  0% {
    transform: translateY(0) scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    opacity: 1;
  }
  3% {
    transform: translateY(5px) scale(0.98);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    opacity: 1;
  }
  8% {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 6px 16px rgba(255, 87, 34, 0.3);
    opacity: 1;
  }
  15% {
    transform: translateY(-50px) scale(1.05);
    box-shadow: 0 8px 24px rgba(255, 87, 34, 0.4);
    opacity: 1;
  }
  30% {
    transform: translateY(-200px) scale(1);
    box-shadow: 0 10px 28px rgba(255, 87, 34, 0.5);
    opacity: 0.95;
  }
  50% {
    transform: translateY(-500px) scale(0.95);
    box-shadow: 0 12px 30px rgba(255, 87, 34, 0.5);
    opacity: 0.9;
  }
  70% {
    transform: translateY(-1000px) scale(0.9);
    box-shadow: 0 14px 32px rgba(255, 87, 34, 0.4);
    opacity: 0.7;
  }
  85% {
    transform: translateY(-2000px) scale(0.8);
    box-shadow: 0 16px 36px rgba(255, 87, 34, 0.3);
    opacity: 0.4;
  }
  100% {
    transform: translateY(-3000px) scale(0.7);
    box-shadow: 0 18px 40px rgba(255, 87, 34, 0.2);
    opacity: 0;
  }
`;

const flameAnimation = keyframes`
  0% {
    height: 30px;
    bottom: -30px;
    width: 20px;
    opacity: 0.9;
  }
  3% {
    height: 40px;
    bottom: -40px;
    width: 22px;
    opacity: 0.95;
  }
  8% {
    height: 60px;
    bottom: -60px;
    width: 24px;
    opacity: 1;
  }
  15% {
    height: 80px;
    bottom: -80px;
    width: 26px;
    opacity: 1;
  }
  30% {
    height: 120px;
    bottom: -120px;
    width: 28px;
    opacity: 1;
  }
  50% {
    height: 160px;
    bottom: -160px;
    width: 30px;
    opacity: 0.95;
  }
  70% {
    height: 200px;
    bottom: -200px;
    width: 32px;
    opacity: 0.9;
  }
  85% {
    height: 240px;
    bottom: -240px;
    width: 34px;
    opacity: 0.7;
  }
  100% {
    height: 280px;
    bottom: -280px;
    width: 36px;
    opacity: 0;
  }
`;

const innerFlameAnimation = keyframes`
  0% {
    height: 25px;
    bottom: -25px;
    width: 12px;
    opacity: 0.9;
  }
  3% {
    height: 35px;
    bottom: -35px;
    width: 14px;
    opacity: 0.95;
  }
  8% {
    height: 45px;
    bottom: -45px;
    width: 16px;
    opacity: 1;
  }
  15% {
    height: 60px;
    bottom: -60px;
    width: 18px;
    opacity: 1;
  }
  30% {
    height: 90px;
    bottom: -90px;
    width: 20px;
    opacity: 1;
  }
  50% {
    height: 120px;
    bottom: -120px;
    width: 22px;
    opacity: 0.95;
  }
  70% {
    height: 150px;
    bottom: -150px;
    width: 24px;
    opacity: 0.9;
  }
  85% {
    height: 180px;
    bottom: -180px;
    width: 26px;
    opacity: 0.7;
  }
  100% {
    height: 210px;
    bottom: -210px;
    width: 28px;
    opacity: 0;
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    background-position: 0% 50%;
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
    background-position: 100% 50%;
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    background-position: 0% 50%;
  }
`;

// 样式化组件
const RocketButton = styled(Fab, {
  shouldForwardProp: (prop) => prop !== 'launching'
})(({ theme, launching }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 1000,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: launching 
    ? `${rocketLaunch} 3s forwards cubic-bezier(0.215, 0.610, 0.355, 1.000)` 
    : `${pulseAnimation} 3s infinite ease-in-out`,
  overflow: 'visible',
  background: launching 
    ? 'linear-gradient(135deg, #4dabf5 0%, #1976d2 100%)' 
    : 'linear-gradient(135deg, #42a5f5 0%, #1976d2 50%, #0d47a1 100%)',
  backgroundSize: '200% 200%',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: launching ? '30px' : '0px',
    background: 'linear-gradient(to bottom, #ff9800, #ff5722 40%, rgba(255,87,34,0.6) 70%, transparent)',
    borderRadius: '30% 30% 60% 60%',
    opacity: launching ? 1 : 0,
    transition: 'all 0.3s',
    filter: 'blur(3px)',
    zIndex: -1,
    animation: launching ? `${flameAnimation} 3s forwards cubic-bezier(0.215, 0.610, 0.355, 1.000)` : 'none',
    transformOrigin: 'center bottom',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-25px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '12px',
    height: launching ? '25px' : '0px',
    background: 'linear-gradient(to bottom, #ffeb3b, #ff9800 30%, transparent)',
    borderRadius: '40% 40% 60% 60%',
    opacity: launching ? 0.9 : 0,
    transition: 'all 0.3s',
    filter: 'blur(2px)',
    zIndex: -1,
    animation: launching ? `${innerFlameAnimation} 3s forwards cubic-bezier(0.215, 0.610, 0.355, 1.000)` : 'none',
    transformOrigin: 'center bottom',
  },
  '&:hover': {
    transform: 'translateY(-3px) scale(1.1)',
    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.4)',
    background: 'linear-gradient(135deg, #64b5f6 0%, #2196f3 50%, #1565c0 100%)',
    backgroundSize: '200% 200%',
    backgroundPosition: '100% 50%',
    '&::before': {
      height: '40px',
      bottom: '-40px',
      opacity: 0.8,
    },
    '&::after': {
      height: '30px',
      bottom: '-30px',
      opacity: 0.7,
    },
  },
  '&:active': {
    transform: 'translateY(1px) scale(0.95)',
    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  }
}));

const RocketIcon = styled(RocketLaunchIcon)({
  transform: 'rotate(-45deg)',
  transition: 'transform 0.3s ease',
  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
});

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const theme = useTheme();
  const animationTimeoutRef = useRef(null);

  // 监听滚动事件，决定按钮是否可见
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        // 如果页面已经回到顶部，重置发射状态
        setIsLaunching(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);

    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // 清除任何现有的超时
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  // 滚动到顶部的函数，添加火箭发射动画
  const scrollToTop = () => {
    // 如果已经在发射中，不要重复触发
    if (isLaunching) return;
    
    // 设置发射状态为true，触发动画
    setIsLaunching(true);
    
    // 延迟一点时间再滚动，让用户看到发射动画
    setTimeout(() => {
      // 计算滚动时间，根据当前滚动位置动态调整
      const scrollDuration = Math.min(Math.max(window.pageYOffset / 3, 800), 2000);
      
      // 使用平滑滚动效果
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 800); // 800ms后开始滚动，给动画留出更多时间
    
    // 动画结束后重置状态
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    
    animationTimeoutRef.current = setTimeout(() => {
      setIsLaunching(false);
    }, 3000); // 动画持续时间后重置
  };

  return (
    <Zoom in={isVisible}>
      <RocketButton
        color="primary"
        size="medium"
        aria-label="scroll back to top"
        onClick={scrollToTop}
        launching={isLaunching}
      >
        <RocketIcon />
      </RocketButton>
    </Zoom>
  );
};

export default ScrollToTop;